/**
 * Neon Candy Fortune — 资源 manifest
 *
 * 来自 candy/asset-plan/ + candy/prompts/ + candy/generated/。
 * Phaser preload 遍历此列表注册图片；业务通过 AssetKeys 引用。
 *
 * 重要：旧的 NC-001..NC-120 manifest 已废弃。当前生效的资源全部在
 * `packages/phasergame/public/assets/candy/` 下，由 candy/tools/crop_sheets.py
 * 从 sheets 切片得到（symbols / frames / backgrounds / overlays 是直接生成的
 * named PNG，无需切片）。
 */

export type AssetCategory =
  | 'symbols'
  | 'frames'
  | 'backgrounds'
  | 'overlays'
  | 'buttons'
  | 'ui'
  | 'fx'
  | 'freespins'
  | 'win'

export interface CandyAsset {
  key: string
  category: AssetCategory
  path: string
}

const BASE = '/assets/candy'

export const CANDY_ASSETS: readonly CandyAsset[] = [
  // ─── Symbols (cluster_tumble 12 张) ──────────────────
  { key: 'sym_cherry',           category: 'symbols', path: `${BASE}/symbols/nc_symbol_cherry_candy.webp` },
  { key: 'sym_lemon',            category: 'symbols', path: `${BASE}/symbols/nc_symbol_lemon_candy.webp` },
  { key: 'sym_grape',            category: 'symbols', path: `${BASE}/symbols/nc_symbol_grape_candy.webp` },
  { key: 'sym_watermelon',       category: 'symbols', path: `${BASE}/symbols/nc_symbol_watermelon_candy.webp` },
  { key: 'sym_strawberry',       category: 'symbols', path: `${BASE}/symbols/nc_symbol_strawberry_candy.webp` },
  { key: 'sym_blueberry',        category: 'symbols', path: `${BASE}/symbols/nc_symbol_blueberry_candy.webp` },
  { key: 'sym_rainbow',          category: 'symbols', path: `${BASE}/symbols/nc_symbol_rainbow_candy.webp` },
  { key: 'sym_heart_gummy',      category: 'symbols', path: `${BASE}/symbols/nc_symbol_heart_gummy.webp` },
  { key: 'sym_chocolate_square', category: 'symbols', path: `${BASE}/symbols/nc_symbol_chocolate_square.webp` },
  { key: 'sym_star_candy',       category: 'symbols', path: `${BASE}/symbols/nc_symbol_star_candy.webp` },
  { key: 'sym_lollipop_scatter', category: 'symbols', path: `${BASE}/symbols/nc_symbol_lollipop_scatter.webp` },
  { key: 'sym_multiplier_orb',   category: 'symbols', path: `${BASE}/symbols/nc_symbol_multiplier_orb_blank.webp` },

  // ─── Frame ───────────────────────────────────────────
  { key: 'frame_grid_6x5',       category: 'frames', path: `${BASE}/frames/nc_frame_grid_6x5_v01.webp` },

  // ─── Backgrounds ─────────────────────────────────────
  { key: 'bg_main',              category: 'backgrounds', path: `${BASE}/backgrounds/nc_bg_main_gameplay_v01.webp` },
  { key: 'bg_loading',           category: 'backgrounds', path: `${BASE}/backgrounds/nc_bg_loading_portal_logo_v01.webp` },
  // bg_free_spins / overlay_big_win 已挪到 CANDY_LAZY_FS_ASSETS / CANDY_LAZY_WIN_ASSETS

  // ─── Spin / generic buttons ──────────────────────────
  { key: 'btn_spin_idle',        category: 'buttons', path: `${BASE}/buttons/nc_spin_idle.webp` },
  { key: 'btn_spin_pressed',     category: 'buttons', path: `${BASE}/buttons/nc_spin_pressed.webp` },
  { key: 'btn_spin_disabled',    category: 'buttons', path: `${BASE}/buttons/nc_spin_disabled.webp` },
  { key: 'btn_stop',             category: 'buttons', path: `${BASE}/buttons/nc_stop_button.webp` },
  { key: 'btn_spin_base_notext', category: 'buttons', path: `${BASE}/buttons/nc_spin_base_notext.webp` },
  { key: 'fx_spin_glow_pink',    category: 'buttons', path: `${BASE}/buttons/nc_spin_glow_pink.webp` },
  { key: 'fx_spin_glow_gold',    category: 'buttons', path: `${BASE}/buttons/nc_spin_glow_gold.webp` },
  { key: 'btn_claim',            category: 'buttons', path: `${BASE}/buttons/nc_btn_claim.webp` },
  { key: 'btn_ok',               category: 'buttons', path: `${BASE}/buttons/nc_btn_ok.webp` },
  { key: 'btn_capsule_purple',   category: 'buttons', path: `${BASE}/buttons/nc_btn_capsule_purple.webp` },

  // ─── UI ──────────────────────────────────────────────
  { key: 'ui_bg_decoration',     category: 'ui', path: `${BASE}/ui/nc_ui_bg_decoration.webp` },
  { key: 'ui_grid_frame_alt',    category: 'ui', path: `${BASE}/ui/nc_ui_grid_frame_alt.webp` },
  { key: 'ui_panel_purple_sq',   category: 'ui', path: `${BASE}/ui/nc_ui_panel_purple_square.webp` },
  { key: 'ui_pillar_purple',     category: 'ui', path: `${BASE}/ui/nc_ui_pillar_purple.webp` },
  { key: 'ui_balance_pill',      category: 'ui', path: `${BASE}/ui/nc_ui_balance_pill.webp` },
  { key: 'ui_bet_pill',          category: 'ui', path: `${BASE}/ui/nc_ui_bet_pill.webp` },
  { key: 'ui_feature_strip',     category: 'ui', path: `${BASE}/ui/nc_ui_feature_strip.webp` },
  { key: 'ui_ribbon_banner',     category: 'ui', path: `${BASE}/ui/nc_ui_ribbon_banner.webp` },
  { key: 'ui_icon_back',         category: 'ui', path: `${BASE}/ui/nc_ui_icon_back.webp` },
  { key: 'ui_icon_settings',     category: 'ui', path: `${BASE}/ui/nc_ui_icon_settings.webp` },
  { key: 'ui_icon_info',         category: 'ui', path: `${BASE}/ui/nc_ui_icon_info.webp` },
  { key: 'ui_icon_close',        category: 'ui', path: `${BASE}/ui/nc_ui_icon_close.webp` },
  { key: 'ui_panel_white_pink',  category: 'ui', path: `${BASE}/ui/nc_ui_panel_white_pink_frame.webp` },
  { key: 'ui_panel_pink_frame',  category: 'ui', path: `${BASE}/ui/nc_ui_panel_pink_frame.webp` },
  { key: 'ui_gen_jackpot_grand_wide', category: 'ui', path: `${BASE}/ui/generated/nc_ui_gen_jackpot_grand_wide.webp` },
  { key: 'ui_gen_balance_pill',       category: 'ui', path: `${BASE}/ui/generated/nc_ui_gen_balance_pill.webp` },
  { key: 'ui_gen_balance_pill_wide',  category: 'ui', path: `${BASE}/ui/generated/nc_ui_gen_balance_pill_wide.webp` },
  { key: 'ui_gen_win_pill',           category: 'ui', path: `${BASE}/ui/generated/nc_ui_gen_win_pill.webp` },
  { key: 'ui_gen_total_bet_pill',     category: 'ui', path: `${BASE}/ui/generated/nc_ui_gen_total_bet_pill.webp` },
  { key: 'ui_gen_spin_button',        category: 'ui', path: `${BASE}/ui/generated/nc_ui_gen_spin_button.webp` },

  // ─── FX ──────────────────────────────────────────────
  { key: 'fx_cell_outline',         category: 'fx', path: `${BASE}/fx/nc_fx_cell_outline.webp` },
  { key: 'fx_cluster_link_h',       category: 'fx', path: `${BASE}/fx/nc_fx_cluster_link_h.webp` },
  { key: 'fx_cluster_link_corner',  category: 'fx', path: `${BASE}/fx/nc_fx_cluster_link_corner.webp` },
  { key: 'fx_cascade_streak',       category: 'fx', path: `${BASE}/fx/nc_fx_cascade_streak.webp` },
  { key: 'fx_burst_icons',          category: 'fx', path: `${BASE}/fx/nc_fx_burst_icons.webp` },
  { key: 'fx_heart_particles',      category: 'fx', path: `${BASE}/fx/nc_fx_heart_particles.webp` },
  { key: 'fx_star_yellow',          category: 'fx', path: `${BASE}/fx/nc_fx_star_yellow_particles.webp` },
  { key: 'fx_sparkles',             category: 'fx', path: `${BASE}/fx/nc_fx_sparkles.webp` },
  { key: 'fx_pink_puff',            category: 'fx', path: `${BASE}/fx/nc_fx_pink_puff.webp` },
  { key: 'fx_falling_streak',       category: 'fx', path: `${BASE}/fx/nc_fx_falling_streak.webp` },
  { key: 'fx_candy_bomb',           category: 'fx', path: `${BASE}/fx/nc_fx_candy_bomb.webp` },
  { key: 'fx_landing_trail',        category: 'fx', path: `${BASE}/fx/nc_fx_landing_trail.webp` },
  { key: 'fx_starburst_yellow',     category: 'fx', path: `${BASE}/fx/nc_fx_starburst_yellow.webp` },
  { key: 'fx_starburst_pink',       category: 'fx', path: `${BASE}/fx/nc_fx_starburst_pink.webp` },
  { key: 'fx_candy_swirl',          category: 'fx', path: `${BASE}/fx/nc_fx_candy_swirl.webp` },

  // Free Spins / Win celebration 资源已挪到 CANDY_LAZY_FS_ASSETS / CANDY_LAZY_WIN_ASSETS（按需 fetch）
] as const

/**
 * Lazy — Big Win 全屏庆祝（≥big level 触发）。约 5.5MB，按需 fetch。
 */
export const CANDY_LAZY_WIN_ASSETS: readonly CandyAsset[] = [
  { key: 'overlay_big_win',      category: 'overlays', path: `${BASE}/overlays/nc_overlay_big_win_bg_v01.webp` },
  { key: 'overlay_win_celebration_generated', category: 'overlays', path: `${BASE}/overlays/nc_overlay_win_celebration_generated_v01.webp` },
  { key: 'overlay_jackpot_generated', category: 'overlays', path: `${BASE}/overlays/nc_overlay_jackpot_generated_v01.webp` },
  { key: 'win_title_big_win_generated',    category: 'win', path: `${BASE}/win/generated/nc_win_title_big_win_generated.webp` },
  { key: 'win_title_mega_win_generated',   category: 'win', path: `${BASE}/win/generated/nc_win_title_mega_win_generated.webp` },
  { key: 'win_title_super_win_generated',  category: 'win', path: `${BASE}/win/generated/nc_win_title_super_win_generated.webp` },
  { key: 'win_title_jackpot_generated',    category: 'win', path: `${BASE}/win/generated/nc_win_title_jackpot_generated.webp` },
  { key: 'win_amount_frame_generated',     category: 'win', path: `${BASE}/win/generated/nc_win_amount_frame_generated.webp` },
  { key: 'win_badge_big_generated',        category: 'win', path: `${BASE}/win/generated/nc_win_badge_big_generated.webp` },
  { key: 'win_badge_mega_generated',       category: 'win', path: `${BASE}/win/generated/nc_win_badge_mega_generated.webp` },
  { key: 'win_badge_super_generated',      category: 'win', path: `${BASE}/win/generated/nc_win_badge_super_generated.webp` },
  { key: 'win_badge_jackpot_generated',    category: 'win', path: `${BASE}/win/generated/nc_win_badge_jackpot_generated.webp` },
  { key: 'win_big_win_title',    category: 'win', path: `${BASE}/win/nc_win_big_win_title.webp` },
  { key: 'win_mega_win_title',   category: 'win', path: `${BASE}/win/nc_win_mega_win_title.webp` },
  { key: 'win_super_win_title',  category: 'win', path: `${BASE}/win/nc_win_super_win_title.webp` },
  { key: 'win_jackpot_title',    category: 'win', path: `${BASE}/win/nc_win_jackpot_title.webp` },
  { key: 'win_gold_frame',       category: 'win', path: `${BASE}/win/nc_win_gold_frame_notext.webp` },
  { key: 'win_pink_frame',       category: 'win', path: `${BASE}/win/nc_win_pink_frame_notext.webp` },
  { key: 'win_gold_coins',       category: 'win', path: `${BASE}/win/nc_win_gold_coins.webp` },
  { key: 'win_pink_candy_burst', category: 'win', path: `${BASE}/win/nc_win_pink_candy_burst.webp` },
  { key: 'win_starburst_yellow', category: 'win', path: `${BASE}/win/nc_win_starburst_yellow.webp` },
  { key: 'win_starburst_pink',   category: 'win', path: `${BASE}/win/nc_win_starburst_pink.webp` },
  { key: 'win_confetti',         category: 'win', path: `${BASE}/win/nc_win_confetti.webp` },
  { key: 'win_btn_ok_pink',      category: 'win', path: `${BASE}/win/nc_win_btn_ok_pink.webp` },
  { key: 'win_btn_pink_notext',  category: 'win', path: `${BASE}/win/nc_win_btn_pink_notext.webp` },
  { key: 'win_btn_purple_circle',category: 'win', path: `${BASE}/win/nc_win_btn_purple_circle.webp` },
  { key: 'win_btn_pink_ring',    category: 'win', path: `${BASE}/win/nc_win_btn_pink_ring.webp` },
] as const

/**
 * Lazy — Free Spins 全屏接管 + FS 模式 bg（≥4 SCATTER 触发）。约 5.5MB，按需 fetch。
 */
export const CANDY_LAZY_FS_ASSETS: readonly CandyAsset[] = [
  { key: 'overlay_win_celebration_generated', category: 'overlays', path: `${BASE}/overlays/nc_overlay_win_celebration_generated_v01.webp` },
  { key: 'overlay_free_spins_generated', category: 'overlays', path: `${BASE}/overlays/nc_overlay_free_spins_generated_v01.webp` },
  { key: 'win_title_free_spins_generated', category: 'win', path: `${BASE}/win/generated/nc_win_title_free_spins_generated.webp` },
  { key: 'win_free_spins_count_frame_generated', category: 'win', path: `${BASE}/win/generated/nc_win_free_spins_count_frame_generated.webp` },
  { key: 'bg_free_spins',        category: 'backgrounds', path: `${BASE}/backgrounds/nc_bg_free_spins_v01.webp` },
  { key: 'fs_title',             category: 'freespins', path: `${BASE}/freespins/nc_fs_title.webp` },
  { key: 'fs_banner_purple_top', category: 'freespins', path: `${BASE}/freespins/nc_fs_banner_purple_top.webp` },
  { key: 'fs_banner_horizontal', category: 'freespins', path: `${BASE}/freespins/nc_fs_banner_horizontal.webp` },
  { key: 'fs_panel_purple_left', category: 'freespins', path: `${BASE}/freespins/nc_fs_panel_purple_left.webp` },
  { key: 'fs_panel_cream_left',  category: 'freespins', path: `${BASE}/freespins/nc_fs_panel_cream_left.webp` },
  { key: 'fs_btn_ok',            category: 'freespins', path: `${BASE}/freespins/nc_fs_btn_ok.webp` },
  { key: 'fs_btn_pink_pill',     category: 'freespins', path: `${BASE}/freespins/nc_fs_btn_pink_pill.webp` },
  { key: 'fs_swirl_bg',          category: 'freespins', path: `${BASE}/freespins/nc_fs_swirl_bg.webp` },
  { key: 'fs_lollipop_left',     category: 'freespins', path: `${BASE}/freespins/nc_fs_lollipop_left.webp` },
  { key: 'fs_lollipop_right',    category: 'freespins', path: `${BASE}/freespins/nc_fs_lollipop_right.webp` },
  { key: 'fs_light_burst',       category: 'freespins', path: `${BASE}/freespins/nc_fs_light_burst.webp` },
  { key: 'fs_candy_drop',        category: 'freespins', path: `${BASE}/freespins/nc_fs_candy_drop.webp` },
  { key: 'fs_ribbon_decor',      category: 'freespins', path: `${BASE}/freespins/nc_fs_ribbon_decor.webp` },
] as const

/**
 * Eager 音频 — 普通 spin / cascade / 小赢 / UI 必备。Loading bgm / win-mode bgm /
 * win-only SFX 已挪到 LAZY。
 */
export const CANDY_AUDIO: readonly { key: string; path: string }[] = [
  // BGM
  { key: 'bgm_main',                path: `${BASE}/audio/bgm/C-BGM01_main_candy_loop.mp3` },
  // Lucky Fever room loop — preloaded so the room-state BGM swap on Fever
  // start is instant (no audible gap while the file fetches).
  { key: 'bgm_lucky_fever_loop',    path: `${BASE}/audio/bgm/C-BGM06_lucky_fever_loop.mp3` },
  // SFX (普通 spin/cascade/UI)
  { key: 'sfx_spin_tap',            path: `${BASE}/audio/sfx/C-SFX-01_spin_tap.mp3` },
  { key: 'sfx_reels_start',         path: `${BASE}/audio/sfx/C-SFX-02_reels_start.mp3` },
  { key: 'sfx_reel_stop',           path: `${BASE}/audio/sfx/C-SFX-03_reel_stop.mp3` },
  { key: 'sfx_cluster_detected',    path: `${BASE}/audio/sfx/C-SFX-04_cluster_detected.mp3` },
  { key: 'sfx_candy_tumble',        path: `${BASE}/audio/sfx/C-SFX-05_candy_tumble.mp3` },
  { key: 'sfx_symbol_burst',        path: `${BASE}/audio/sfx/C-SFX-06_symbol_burst.mp3` },
  { key: 'sfx_small_win',           path: `${BASE}/audio/sfx/C-SFX-09_small_win.mp3` },
  { key: 'sfx_error_soft',          path: `${BASE}/audio/sfx/C-SFX-14_error_soft.mp3` },
  { key: 'sfx_button_disabled',     path: `${BASE}/audio/sfx/C-SFX-15_button_disabled.mp3` },
  // Fever ignite + anticipation pulse — preloaded; both fire at fever
  // moments and need to be ready when the canvas flash hits.
  { key: 'sfx_fever_ignite',                path: `${BASE}/audio/sfx/C-SFX16_fever_ignite.mp3` },
  { key: 'sfx_fever_anticipation_pulse',    path: `${BASE}/audio/sfx/C-SFX17_fever_anticipation_pulse.mp3` },
] as const

/** Lazy 音频 — Big Win 庆祝。约 700K，按需 fetch。 */
export const CANDY_LAZY_WIN_AUDIO: readonly { key: string; path: string }[] = [
  { key: 'bgm_big_win_stinger',     path: `${BASE}/audio/bgm/C-BGM03_big_win_stinger.mp3` },
  { key: 'bgm_mega_win_stinger',    path: `${BASE}/audio/bgm/C-BGM04_mega_win_stinger.mp3` },
  { key: 'bgm_super_win_stinger',   path: `${BASE}/audio/bgm/C-BGM08_super_win_stinger.mp3` },
  { key: 'bgm_jackpot_stinger',     path: `${BASE}/audio/bgm/C-BGM09_jackpot_stinger.mp3` },
  { key: 'bgm_fever_settlement',    path: `${BASE}/audio/bgm/C-BGM07_fever_settlement.mp3` },
  { key: 'sfx_big_win_count',       path: `${BASE}/audio/sfx/C-SFX-10_big_win_count_loop.mp3` },
] as const

/** Lazy 音频 — Free Spins 模式。约 700K，按需 fetch。 */
export const CANDY_LAZY_FS_AUDIO: readonly { key: string; path: string }[] = [
  { key: 'bgm_free_spins',          path: `${BASE}/audio/bgm/C-BGM02_free_spins_mode.mp3` },
  { key: 'sfx_free_spins_trigger',  path: `${BASE}/audio/sfx/C-SFX-11_free_spins_trigger.mp3` },
  { key: 'sfx_free_spin_start',     path: `${BASE}/audio/sfx/C-SFX-12_free_spin_start.mp3` },
  { key: 'sfx_bonus_summary',       path: `${BASE}/audio/sfx/C-SFX-13_bonus_summary.mp3` },
] as const

/** 未在当前代码使用的音频，留作引用，不进 preload */
export const CANDY_UNUSED_AUDIO: readonly { key: string; path: string }[] = [
  { key: 'bgm_loading',             path: `${BASE}/audio/bgm/C-BGM05_loading_transition.mp3` },
  { key: 'sfx_multiplier_appears',  path: `${BASE}/audio/sfx/C-SFX-07_multiplier_appears.mp3` },
  { key: 'sfx_multiplier_fly_in',   path: `${BASE}/audio/sfx/C-SFX-08_multiplier_fly_in.mp3` },
] as const
